/**
 * MonthlyReportsPage — unified "דוחות" dashboard (clinic + finance tabs).
 *
 * Monthly reports are LIVE server-maintained docs at
 * clinics/{id}/monthlyReports/{YYYY-MM} (schema v3). Missing/stale months are
 * lazily generated via ensureMonthlyReport; the live query then delivers the
 * doc through its onSnapshot subscription.
 *
 * Tabs come from components/dashboard/reports/sectionRegistry.js.
 * Deep links (/dashboard/reports/:monthKey) pass a monthKey prop via
 * MonthlyReportPage.
 *
 * Error codes: CK-3710 (ensure report generation).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ChartLineUp } from "@phosphor-icons/react";
import {
  selectClinicId,
  selectTherapistId,
  selectIsAdmin,
  selectHasPermission,
  selectClinicProfile,
  selectCanAccessFinance,
  selectCanExportData,
} from "../../store/slices/authSlice";
import {
  useGetMonthlyReportQuery,
  useGetMonthlyReportsListQuery,
  useEnsureMonthlyReportMutation,
} from "../../store/services/monthlyReportsApi";
import sectionRegistry from "../../components/dashboard/reports/sectionRegistry";
import ReportsMonthNav, {
  getIsraelNowYearMonth,
} from "../../components/dashboard/reports/ReportsMonthNav";
import { PageHeader } from "../../components/shared";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import ContentLoader from "../../components/shared/ContentLoader";
import "./MonthlyReportPage.css";
import "./MonthlyReportsPage.css";

const MONTH_KEY_RE = /^\d{4}-\d{2}$/;

const parseMonthKey = (monthKey) => {
  if (!monthKey || !MONTH_KEY_RE.test(monthKey)) return null;
  const [year, month] = monthKey.split("-").map(Number);
  if (month < 1 || month > 12) return null;
  return { year, month };
};

const MonthlyReportsPage = ({ monthKey: monthKeyProp }) => {
  const navigate = useNavigate();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const isAdmin = useSelector(selectIsAdmin);
  // Permissions v2: clinic-wide report scope (admin, or reports.viewClinicWide)
  const canViewClinicWideReports = useSelector(
    selectHasPermission("reports.viewClinicWide")
  );
  const hasClinicWideScope = isAdmin || canViewClinicWideReports;
  const clinicProfile = useSelector(selectClinicProfile);
  const canAccessFinance = useSelector(selectCanAccessFinance);
  const canExportData = useSelector(selectCanExportData);

  // Page guard: admins always; non-admin therapists only in multi-therapist
  // clinics (solo clinic → owner is the sole therapist and is admin).
  const isBlocked = !isAdmin && clinicProfile?.isSoloClinic === true;
  useEffect(() => {
    if (isBlocked) navigate("/dashboard", { replace: true });
  }, [isBlocked, navigate]);

  // ─── Selected month — PER TAB (deep-link prop or current Israel month) ───
  // Every tab lazily starts from the same initial value; afterwards each tab
  // remembers its own month independently.
  const [initialSelected] = useState(() => {
    const fromProp = parseMonthKey(monthKeyProp);
    const now = getIsraelNowYearMonth();
    if (!fromProp) return now;
    // Clamp future deep links to the current month
    if (
      fromProp.year > now.year ||
      (fromProp.year === now.year && fromProp.month > now.month)
    ) {
      return now;
    }
    return fromProp;
  });
  const [selectedByTab, setSelectedByTab] = useState({});

  // ─── Tabs ───
  const permCtx = useMemo(
    () => ({ canAccessFinance, isAdmin }),
    [canAccessFinance, isAdmin]
  );
  const visibleSections = useMemo(
    () => sectionRegistry.filter((s) => s.isVisible(permCtx)),
    [permCtx]
  );
  const [activeTabId, setActiveTabId] = useState(null);
  const activeSection =
    visibleSections.find((s) => s.id === activeTabId) || visibleSections[0];
  const activeTabKey = activeSection?.id || "clinic";
  const activeTabKeyRef = useRef(activeTabKey);
  activeTabKeyRef.current = activeTabKey;

  // Sync when the deep-link param changes — active tab's entry only
  useEffect(() => {
    const fromProp = parseMonthKey(monthKeyProp);
    if (!fromProp) return;
    setSelectedByTab((prev) => {
      const tabKey = activeTabKeyRef.current;
      const cur = prev[tabKey] || initialSelected;
      return cur.year === fromProp.year && cur.month === fromProp.month
        ? prev
        : { ...prev, [tabKey]: fromProp };
    });
  }, [monthKeyProp, initialSelected]);

  const selected = selectedByTab[activeTabKey] || initialSelected;
  const { year, month } = selected;
  const monthKey = `${year}-${String(month).padStart(2, "0")}`;

  // ─── Data ───
  const {
    data: report,
    isLoading: reportLoading,
    isFetching: reportFetching,
    error: reportError,
    refetch: refetchReport,
  } = useGetMonthlyReportQuery(
    { clinicId, monthKey, clinicWide: hasClinicWideScope },
    { skip: !clinicId }
  );

  const { data: reportsList } = useGetMonthlyReportsListQuery(
    { clinicId, limit: 24, clinicWide: hasClinicWideScope },
    { skip: !clinicId }
  );

  // ─── Lazy generation for missing / stale (schema < 5) months ───
  const [ensureMonthlyReport, { isLoading: ensurePending }] =
    useEnsureMonthlyReportMutation();
  const ensureAttemptedRef = useRef(new Set());
  const [ensureFailedKey, setEnsureFailedKey] = useState(null);

  const isStale = !!report && (report.meta?.schemaVersion || 0) < 5;
  const needsEnsure =
    !!clinicId && !reportLoading && !reportFetching && (!report || isStale);

  useEffect(() => {
    if (!needsEnsure) return;
    if (ensureAttemptedRef.current.has(monthKey)) return;
    ensureAttemptedRef.current.add(monthKey);
    (async () => {
      try {
        await ensureMonthlyReport({ clinicId, year, month }).unwrap();
      } catch (error) {
        console.error("[CK-3710] Error ensuring monthly report generation:", {
          errorCode: "CK-3710",
          errorMessage: error?.message,
          clinicId,
          monthKey,
          timestamp: new Date().toISOString(),
        });
        setEnsureFailedKey(monthKey);
      }
    })();
  }, [needsEnsure, monthKey, clinicId, year, month, ensureMonthlyReport]);

  const handleRetryEnsure = () => {
    ensureAttemptedRef.current.delete(monthKey);
    setEnsureFailedKey(null);
    refetchReport();
  };

  // ─── Therapist scope (page level) ───
  const isSoloClinic =
    clinicProfile?.isSoloClinic === true || report?.meta?.isSoloClinic === true;

  const therapistOptions = useMemo(() => {
    if (!report?.perTherapist) return [];
    return Object.entries(report.perTherapist).map(([id, data]) => ({
      id,
      name: data.therapistName || "מטפל/ת",
      inactive: data.inactive === true,
    }));
  }, [report]);

  const [adminScope, setAdminScope] = useState(null);
  const effectiveScope = useMemo(() => {
    if (isSoloClinic) return null;
    if (!hasClinicWideScope) return therapistId || null; // pinned to own slice
    return adminScope;
  }, [isSoloClinic, hasClinicWideScope, therapistId, adminScope]);

  const showScopeSelector =
    hasClinicWideScope && !isSoloClinic && therapistOptions.length > 0;

  if (isBlocked) return null;

  // ─── Body states ───
  const preparing =
    !report && (ensurePending || (needsEnsure && ensureFailedKey !== monthKey));
  const showEnsureError = !report && ensureFailedKey === monthKey;
  const showQueryError = !report && !!reportError && !preparing;

  const ActiveComponent = activeSection?.Component;
  // Standalone sections (e.g. patient reports) don't consume the monthly
  // report doc — render them directly, skipping the report loading/preparing
  // gating and the shared month nav.
  const isStandaloneSection = activeSection?.standalone === true;

  let body = null;
  if (isStandaloneSection && ActiveComponent) {
    body = (
      <ActiveComponent
        clinicId={clinicId}
        scopeTherapistId={effectiveScope}
        canExportData={canExportData}
        canAccessFinance={canAccessFinance}
      />
    );
  } else if (reportLoading) {
    body = (
      <ContentLoader fill message="טוען דוח..." />
    );
  } else if (preparing) {
    body = (
      <div className="rp-preparing">
        <LoaderLoop size="md" />
        <span>מכין את הדוח...</span>
      </div>
    );
  } else if (showEnsureError || showQueryError) {
    body = (
      <div className="mr-error">
        <h2>לא ניתן לטעון את הדוח</h2>
        <p>אירעה שגיאה בהכנת הדוח החודשי. נסו שוב בעוד רגע.</p>
        <button type="button" className="mr-retry-btn" onClick={handleRetryEnsure}>
          נסה שוב
        </button>
      </div>
    );
  } else if (ActiveComponent) {
    body = (
      <ActiveComponent
        clinicId={clinicId}
        monthKey={monthKey}
        year={year}
        month={month}
        report={report}
        reportsList={reportsList}
        scopeTherapistId={effectiveScope}
        canExportData={canExportData}
      />
    );
  }

  return (
    <div className="monthly-report-page">
      {/* Header */}
      <PageHeader
        icon={ChartLineUp}
        title="דוחות"
        actions={
          showScopeSelector ? (
            <select
              className="mr-scope-select"
              value={adminScope || ""}
              onChange={(e) => setAdminScope(e.target.value || null)}
              aria-label="בחר מטפל/ת"
            >
              <option value="">כל המטפלים</option>
              {therapistOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.inactive ? " (לא פעיל)" : ""}
                </option>
              ))}
            </select>
          ) : null
        }
      />

      {/* Tab strip */}
      {visibleSections.length > 1 && (
        <div className="rp-tabs" role="tablist" aria-label="סוגי דוחות">
          {visibleSections.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeSection?.id === id}
              className={`rp-tab ${
                activeSection?.id === id ? "rp-tab--active" : ""
              }`}
              onClick={() => setActiveTabId(id)}
            >
              <Icon size={16} weight="fill" />
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Month navigation — bound to the active tab's own month.
          Standalone sections own their date range and skip it. */}
      {!isStandaloneSection && (
        <ReportsMonthNav
          year={year}
          month={month}
          onChange={(y, m) =>
            setSelectedByTab((prev) => ({
              ...prev,
              [activeTabKey]: { year: y, month: m },
            }))
          }
          computedAt={report?.meta?.computedAt}
        />
      )}

      {body}
    </div>
  );
};

export default MonthlyReportsPage;
